import React, { useEffect, useState, useCallback } from 'react';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { useAuthStore } from '../stores/authStore';
import { WorkspaceSwitcherModal } from './navigation/WorkspaceSwitcherModal';
import type { WorkspaceMemberRole } from '@mellify/shared';

/**
 * WorkspaceInitializer
 *
 * Handles:
 * 1. Auto-selecting a workspace on login (last active from localStorage, or first available)
 * 2. Syncing workspace list from API into the Zustand store
 * 3. Setting the current member role for the active workspace
 * 4. Registering the Cmd/Ctrl+K keyboard shortcut for the quick workspace switcher modal
 */
export const WorkspaceInitializer: React.FC = () => {
  const [isQuickSwitchOpen, setIsQuickSwitchOpen] = useState(false);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: workspaces, isSuccess } = useWorkspaces();
  const {
    currentWorkspace,
    setCurrentWorkspace,
    setCurrentMemberRole,
    setWorkspaces,
  } = useWorkspaceStore();

  // Sync workspace list from API to Zustand store when data arrives
  useEffect(() => {
    if (isSuccess && workspaces && isAuthenticated) {
      setWorkspaces(workspaces);

      // Auto-select workspace if none is currently set
      if (!currentWorkspace && workspaces.length > 0) {
        setCurrentWorkspace(workspaces[0]!);
      }

      // If current workspace is no longer in the list, switch to first
      if (currentWorkspace && !workspaces.find((w) => w.id === currentWorkspace.id)) {
        setCurrentWorkspace(workspaces[0] || null);
      }
    }
  }, [isSuccess, workspaces, isAuthenticated, currentWorkspace, setCurrentWorkspace, setWorkspaces]);

  // Set the current member role whenever the active workspace or workspace list changes
  useEffect(() => {
    if (isSuccess && workspaces && currentWorkspace) {
      const activeWorkspace = workspaces.find((w) => w.id === currentWorkspace.id);
      if (activeWorkspace && (activeWorkspace as any).memberRole) {
        setCurrentMemberRole((activeWorkspace as any).memberRole as WorkspaceMemberRole);
      }
    }
  }, [isSuccess, workspaces, currentWorkspace, setCurrentMemberRole]);

  // Register Cmd/Ctrl+K keyboard shortcut for quick workspace switch
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isAuthenticated) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setIsQuickSwitchOpen((prev) => !prev);
      }
    },
    [isAuthenticated]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);

  return (
    <WorkspaceSwitcherModal
      isOpen={isQuickSwitchOpen}
      onClose={() => setIsQuickSwitchOpen(false)}
    />
  );
};

WorkspaceInitializer.displayName = 'WorkspaceInitializer';